import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader, EmptyState } from "@/components/Common";
import { MapPin, Star, BadgeCheck, Search, Store, ArrowLeft } from "lucide-react";

export default function Marketplace() {
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);

  const load = useCallback(async () => {
    setRes(null);
    const params = new URLSearchParams();
    if (cat) params.set("category", cat);
    if (q) params.set("q", q);
    const { data } = await axios.get(`${API}/marketplace/vendors?${params}`);
    setRes(data);
  }, [cat, q]);

  useEffect(() => { axios.get(`${API}/marketplace/categories`).then(({ data }) => setCats(data)); }, []);
  useEffect(() => { load(); }, [cat]);

  return (
    <div className="min-h-screen bg-background">
      <header className="h-20 flex items-center justify-between px-6 lg:px-12 max-w-7xl mx-auto">
        <Link to={user?.wedding_id ? "/app" : "/"} className="flex items-center gap-2 text-sm text-stone-600">
          <ArrowLeft className="w-4 h-4" /> {user?.wedding_id ? "Retour à l'espace" : "Accueil"}</Link>
        <div className="font-serif text-lg">Prestataires romands</div>
      </header>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
        <div className="text-center py-8">
          <div className="eyebrow mb-3">Marketplace · Suisse romande</div>
          <h1 className="font-serif text-4xl lg:text-5xl">Trouvez vos prestataires d'exception</h1>
        </div>
        <div className="flex gap-2 max-w-xl mx-auto mb-8">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="mp-search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Rechercher un prestataire…" className="pl-9 h-11" /></div>
          <Button onClick={load} className="h-11 rounded-full px-6">Rechercher</Button>
        </div>
        <div className="flex gap-2 flex-wrap justify-center mb-10">
          <button onClick={() => setCat("")} className={`px-4 py-1.5 rounded-full text-sm border ${!cat ? "bg-stone-900 text-white" : "bg-white text-stone-600"}`}>Tous</button>
          {cats.slice(0, 12).map((c) => (
            <button key={c} data-testid={`mp-cat-${c}`} onClick={() => setCat(c)}
              className={`px-4 py-1.5 rounded-full text-sm border ${cat === c ? "bg-stone-900 text-white" : "bg-white text-stone-600"}`}>{c}</button>
          ))}
        </div>
        {res === null ? <Loader /> : res.vendors.length === 0 ? (
          <EmptyState icon={Store} title="Aucun prestataire trouvé" description="Essayez d'autres filtres ou catégories." />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {res.vendors.map((v) => (
              <Link key={v.id} to={`/marketplace/${v.slug}`} data-testid={`mp-vendor-${v.id}`}
                className="card-lux overflow-hidden group">
                <div className="relative h-52 overflow-hidden">
                  <img src={v.cover_image || "https://images.unsplash.com/photo-1519741497674-611481863552?w=940&q=80"} alt={v.business_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  {v.sponsored && <span className="absolute top-3 left-3 bg-champagne text-stone-900 text-xs font-medium px-2.5 py-1 rounded-full" data-testid="sponsored-badge">Sponsorisé</span>}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-serif text-lg text-stone-900">{v.business_name}</h3>
                        {v.verified && <BadgeCheck className="w-4 h-4 text-sage" />}</div>
                      <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {v.city}, {v.canton} · {v.category}</div>
                    </div>
                    <div className="flex items-center gap-1 text-sm"><Star className="w-4 h-4 fill-champagne text-champagne" />
                      <span className="font-medium">{v.rating}</span><span className="text-stone-400 text-xs">({v.review_count})</span></div>
                  </div>
                  <div className="mt-3 text-sm text-stone-600">Dès <b className="font-mono">{chf(v.starting_price)}</b></div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
